/**
 * Reader for the DN state-effect parameter grammar:
 * `;` fields, `[...]` bracket groups, `|` sections, `,` sublists, `/` pairs, `_` splits.
 * Tolerant of missing tails (many blows are arity-overloaded).
 */
export class ParamReader {
    public readonly raw: string;
    private offset: number = 0;
    /** Absolute offset of this reader's `raw` (post-trim) within the ORIGINAL top-level string. */
    private readonly absOffset: number;

    /**
     * @param raw The field-grammar string. Internally trimmed; `raw` stays the trimmed string.
     * @param base Absolute offset of the UNTRIMMED `raw` argument within the original top-level
     *   string. Used to compose spans for sub-readers built over `[...]` group contents
     *   (see `groupsWithSpans`).
     */
    constructor(raw: string, base: number = 0) {
        const input = raw == null ? '' : raw;
        this.raw = input.trim();
        const leadingWs = input.match(/^\s*/);
        const trimOffset = leadingWs ? leadingWs[0].length : 0;
        this.absOffset = base + trimOffset;
    }

    /** All delim-separated fields of the WHOLE raw string, trimmed. Ignores sequential offset. */
    public fields(delim: string = ';'): string[] {
        if (this.raw === '') {
            return [];
        }
        return this.raw.split(delim).map((s) => s.trim());
    }

    /** fields() as numbers; blank/junk fields → NaN */
    public numbers(delim: string = ';'): number[] {
        return this.fields(delim).map(ParamReader.num);
    }

    /** Sequential: take the next delim-separated token from the current offset. */
    public next(delim: string = ';'): string {
        const idx = this.raw.indexOf(delim, this.offset);
        if (idx === -1) {
            const ret = this.raw.slice(this.offset);
            this.offset = this.raw.length + 1;
            return ret.trim();
        }
        const ret = this.raw.slice(this.offset, idx);
        this.offset = idx + delim.length;
        return ret.trim();
    }

    public hasNext(): boolean {
        return this.offset <= this.raw.length && this.raw.length > 0;
    }

    public nextNum(delim: string = ';'): number {
        return ParamReader.num(this.next(delim));
    }

    /** Sequential: if the cursor sits on `[`, consume through `]` and return the inside. */
    public nextGroup(): string | null {
        if (this.raw[this.offset] !== '[') {
            return null;
        }
        const end = this.raw.indexOf(']', this.offset + 1);
        if (end === -1) {
            return null;
        }
        const ret = this.raw.slice(this.offset + 1, end);
        this.offset = end + 1;
        return ret;
    }

    /** Everything from the current offset onward. */
    public rest(): string {
        return this.raw.slice(Math.min(this.offset, this.raw.length));
    }

    /** All leading [..] groups plus whatever follows the last group. Ignores offset. */
    public groups(): { groups: string[]; rest: string } {
        const groups: string[] = [];
        let pos = 0;
        while (this.raw[pos] === '[') {
            const end = this.raw.indexOf(']', pos + 1);
            if (end === -1) {
                break;
            }
            groups.push(this.raw.slice(pos + 1, end));
            pos = end + 1;
        }
        return { groups, rest: this.raw.slice(pos) };
    }

    /**
     * Absolute [start,end) of the i-th delim-separated field's TRIMMED token, in the ORIGINAL
     * (untrimmed, un-based) top-level string. Null when the index is out of range, or the field
     * (after trimming) is empty.
     */
    public span(i: number, delim: string = ';'): [number, number] | null {
        if (this.raw === '') {
            return null;
        }
        const parts = this.raw.split(delim);
        if (i < 0 || i >= parts.length) {
            return null;
        }
        let pos = 0;
        for (let j = 0; j < i; j++) {
            pos += parts[j].length + delim.length;
        }
        const rawField = parts[i];
        const trimmed = rawField.trim();
        if (trimmed === '') {
            return null;
        }
        const leadingWs = rawField.match(/^\s*/);
        const start = pos + (leadingWs ? leadingWs[0].length : 0);
        return [this.absOffset + start, this.absOffset + start + trimmed.length];
    }

    /**
     * Like `groups()`, but each group's content and the trailing rest also report their absolute
     * start offset in the original top-level string, so `new ParamReader(g.text, g.start)`
     * composes correct absolute spans for fields inside the bracket group.
     */
    public groupsWithSpans(): { groups: Array<{ text: string; start: number }>; rest: string; restStart: number } {
        const groups: Array<{ text: string; start: number }> = [];
        let pos = 0;
        while (this.raw[pos] === '[') {
            const end = this.raw.indexOf(']', pos + 1);
            if (end === -1) {
                break;
            }
            groups.push({ text: this.raw.slice(pos + 1, end), start: this.absOffset + pos + 1 });
            pos = end + 1;
        }
        return { groups, rest: this.raw.slice(pos), restStart: this.absOffset + pos };
    }

    /** Split the whole raw string at the LAST occurrence of delim. Null if absent. */
    public splitLast(delim: string): [string, string] | null {
        const idx = this.raw.lastIndexOf(delim);
        if (idx === -1) {
            return null;
        }
        return [this.raw.slice(0, idx), this.raw.slice(idx + delim.length)];
    }

    public static num(s: string | undefined | null): number {
        if (s == null || s === '') {
            return NaN;
        }
        return Number(s);
    }

    public static list(s: string, delim: string = ','): string[] {
        if (s == null || s.trim() === '') {
            return [];
        }
        return s.split(delim).map((v) => v.trim());
    }

    /** Numeric list; non-numeric entries are dropped. */
    public static numList(s: string, delim: string = ','): number[] {
        return ParamReader.list(s, delim).map(ParamReader.num).filter((v) => !isNaN(v));
    }
}
